// src/lib/api.ts

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "/api/v1";

const CUSTOM_DOMAINS: Record<string, string> = {
  "bestbdbazar.com": "bestbdbazar",
  "buynarabd.com": "buynarabd",
  "buynara.com": "buynarabd",
};

export function resolveClientSubdomain(explicitSubdomain?: string): string {
  if (explicitSubdomain && explicitSubdomain.trim()) {
    return explicitSubdomain.trim().toLowerCase();
  }
  if (typeof window === "undefined") return "";

  const host = window.location.hostname.split(":")[0].toLowerCase().trim();
  const cleanHost = host.replace(/^www\./, "");
  const pathParts = window.location.pathname.split("/").filter(Boolean);

  if (cleanHost === "localhost" || cleanHost === "127.0.0.1") {
    const reserved = [
      "admin",
      "login",
      "dashboard",
      "home",
      "api",
      "cart",
      "checkout",
      "products",
      "categories",
      "blogs",
    ];
    if (pathParts.length > 0 && !reserved.includes(pathParts[0].toLowerCase())) {
      return pathParts[0].toLowerCase();
    }
    return localStorage.getItem("xylo_active_subdomain") || "";
  }

  if (cleanHost.endsWith(".localhost")) {
    return cleanHost.split(".localhost")[0];
  }

  if (cleanHost.includes("shop.xylotechsolution.com")) {
    const sub = cleanHost.split(".shop.xylotechsolution.com")[0];
    if (sub && sub !== "www" && sub !== "shop") return sub;
  }

  if (CUSTOM_DOMAINS[cleanHost]) {
    return CUSTOM_DOMAINS[cleanHost];
  }

  if (
    cleanHost !== "xylotechsolution.com" &&
    cleanHost !== "shop.xylotechsolution.com" &&
    cleanHost.includes(".")
  ) {
    return cleanHost.split(".")[0];
  }

  return localStorage.getItem("xylo_active_subdomain") || "";
}

/**
 * Localhost ও Production multi-tenant subdomain URL resolve helper
 */
export function getApiBaseUrl(subdomain?: string): string {
  if (typeof window === "undefined") {
    return API_BASE_URL;
  }

  const host = window.location.hostname;
  const isLocal =
    host.includes("localhost") ||
    host === "127.0.0.1" ||
    host.endsWith(".localhost");

  if (isLocal) {
    const sub = resolveClientSubdomain(subdomain) || "nextlook";
    return `http://${sub}.localhost:8000`;
  }

  return "";
}

function buildFinalEndpoint(endpoint: string, subdomain?: string): string {
  if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
    return endpoint;
  }

  const base = getApiBaseUrl(subdomain);
  let cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  // যদি endpoint-এ /api/ না থাকে (যেমন /customers/google/ বা /orders/), তবে অটোমেটিক /api/v1 যোগ করবে
  if (!cleanEndpoint.startsWith("/api/")) {
    cleanEndpoint = `/api/v1${cleanEndpoint}`;
  }

  return `${base}${cleanEndpoint}`;
}

export interface ApiResponse<T = any> extends Response {
  data: T;
}

/**
 * Universal Authenticated API Fetch Wrapper
 */
export async function apiFetch(
  endpoint: string,
  options: RequestInit = {},
  branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
  subdomain?: string
): Promise<ApiResponse> {
  const isBrowser = typeof window !== "undefined";

  let currentBranchSlug = typeof branchOrOptions === "string" ? branchOrOptions : "";
  const extraHeaders =
    branchOrOptions && typeof branchOrOptions === "object" && branchOrOptions.headers
      ? branchOrOptions.headers
      : {};

  const currentSubdomain = resolveClientSubdomain(
    subdomain || extraHeaders["X-Tenant-Subdomain"] || extraHeaders["x-tenant-subdomain"]
  );

  if (isBrowser && !currentBranchSlug && window.location.pathname.includes("/b/")) {
    const parts = window.location.pathname.split("/b/")[1]?.split("/");
    if (parts && parts[0]) {
      currentBranchSlug = parts[0].split("?")[0];
    }
  }

  // ১. Token retrieve করা
  let token = "";
  if (isBrowser) {
    if (currentBranchSlug) {
      const branchKey = `branch_auth_${currentSubdomain}_${currentBranchSlug}`;
      const branchAuthRaw = localStorage.getItem(branchKey);
      if (branchAuthRaw) {
        try {
          const parsed = JSON.parse(branchAuthRaw);
          token = parsed.token || "";
        } catch {}
      }
      if (!token) {
        token = localStorage.getItem(`branch_token_${currentBranchSlug}`) || "";
      }
    }

    if (!token) {
      token =
        (currentSubdomain && localStorage.getItem(`xylo_token_${currentSubdomain}`)) ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("merchant_token") ||
        "";
    }
  }

  // ২. Full URL build করা
  let finalUrl = buildFinalEndpoint(endpoint, currentSubdomain);
  if (branchOrOptions && typeof branchOrOptions === "object" && branchOrOptions.params) {
    const qs = new URLSearchParams();
    Object.entries(branchOrOptions.params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    });
    const qsStr = qs.toString();
    if (qsStr) {
      finalUrl += (finalUrl.includes("?") ? "&" : "?") + qsStr;
    }
  }

  // ৩. Request Headers configure করা
  const headers = new Headers(options.headers || {});
  Object.entries(extraHeaders).forEach(([k, v]) => {
    if (v !== undefined && v !== null) headers.set(k, String(v));
  });

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  if (currentSubdomain && !headers.has("X-Tenant-Subdomain")) {
    headers.set("X-Tenant-Subdomain", currentSubdomain);
  }

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token.trim()}`);
  }

  if (isBrowser) {
    const activeBranchId = localStorage.getItem("active_branch_id");
    if (activeBranchId && !headers.has("X-Branch-ID")) {
      headers.set("X-Branch-ID", String(activeBranchId));
    }
  }

  // ৪. API Call execute করা
  const response = (await fetch(finalUrl, {
    ...options,
    headers,
  })) as ApiResponse;

  let parsedData: any = null;
  try {
    const text = await response.clone().text();
    parsedData = text ? JSON.parse(text) : null;
  } catch {
    parsedData = null;
  }
  response.data = parsedData;

  return response;
}

async function axiosCompatibleRequest(
  endpoint: string,
  options: RequestInit = {},
  branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
  subdomain?: string
): Promise<ApiResponse> {
  const res = await apiFetch(endpoint, options, branchOrOptions, subdomain);
  if (!res.ok) {
    const err: any = new Error(
      res.data?.error || res.data?.detail || res.data?.message || `HTTP Error ${res.status}`
    );
    err.status = res.status;
    err.response = {
      status: res.status,
      data: res.data,
    };
    throw err;
  }
  return res;
}

/**
 * Direct Method Helpers (GET, POST, PATCH, PUT, DELETE) — Axios ও Fetch উভয় স্টাইল সাপোর্ট করে
 */
export const api = {
  get: (
    url: string,
    branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
    subdomain?: string
  ) => axiosCompatibleRequest(url, { method: "GET" }, branchOrOptions, subdomain),

  post: (
    url: string,
    body?: any,
    branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
    subdomain?: string
  ) =>
    axiosCompatibleRequest(
      url,
      {
        method: "POST",
        headers: body instanceof FormData ? {} : { "Content-Type": "application/json" },
        body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      },
      branchOrOptions,
      subdomain
    ),

  patch: (
    url: string,
    body?: any,
    branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
    subdomain?: string
  ) =>
    axiosCompatibleRequest(
      url,
      {
        method: "PATCH",
        headers: body instanceof FormData ? {} : { "Content-Type": "application/json" },
        body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      },
      branchOrOptions,
      subdomain
    ),

  put: (
    url: string,
    body?: any,
    branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
    subdomain?: string
  ) =>
    axiosCompatibleRequest(
      url,
      {
        method: "PUT",
        headers: body instanceof FormData ? {} : { "Content-Type": "application/json" },
        body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      },
      branchOrOptions,
      subdomain
    ),

  delete: (
    url: string,
    branchOrOptions?: string | { headers?: Record<string, string>; params?: Record<string, any> },
    subdomain?: string
  ) => axiosCompatibleRequest(url, { method: "DELETE" }, branchOrOptions, subdomain),
};

export default api;